import React, { FC, ReactNode, useCallback, useEffect, useRef, useLayoutEffect } from 'react';
import { useSelector } from 'react-redux';

import { getStdForm } from 'data/StdForm/selectors';

import withStdDialog from '../StdDialogDeprecated';

interface StdFormDialogWrappedRef {
  handleClose: (event: any, data?: any) => void;
}

interface StdFormDialogProps {
  children?: ReactNode;
  closeWhenSubmitFailed?: boolean;
  onClosed?: (event: any, data?: any) => void;

  // from withStdForm
  formId: string;

  // from StdDialog
  closeDialog: () => void;
  onRef: (ref: StdFormDialogWrappedRef) => void;
}

const StdFormDialog: FC<StdFormDialogProps> = ({
  children,
  closeWhenSubmitFailed,
  onClosed,
  formId,
  closeDialog,
  onRef,
}) => {
  const stdFormData = useSelector((state) => getStdForm(state, formId));

  const latest = useRef({ closeWhenSubmitFailed, onClosed, closeDialog });
  useEffect(() => {
    latest.current = { closeWhenSubmitFailed, onClosed, closeDialog };
  });

  // Called both locally and by the StdDialog HOC when onClose is called.
  const handleClose = useCallback((dEvent: any, data: any = null) => {
    latest.current.closeDialog();
    if (latest.current.onClosed) {
      latest.current.onClosed(dEvent, data);
    }
  }, []);

  useLayoutEffect(() => {
    onRef({ handleClose });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (stdFormData.submitSucceeded) {
      handleClose('SUBMIT_SUCCESS', stdFormData.submitResponseData);
    } else if (closeWhenSubmitFailed && stdFormData.submitFailed) {
      handleClose('SUBMIT_FAILED', stdFormData.submitResponseData);
    } else if (stdFormData.cancelled) {
      handleClose('CANCELLED');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stdFormData]);

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment -- children may be an array with no single wrapping element
    <>
      {children}
    </>
  );
};

export default withStdDialog()(StdFormDialog);
